
import { motion } from 'framer-motion';
import { Clock, ArrowRight, RefreshCw, Loader2, Sparkles } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import ReactMarkdown from 'react-markdown';
import { WorkoutExercise } from '@/types/workout';

interface WorkoutExerciseContentProps {
  currentExercise: WorkoutExercise;
  userAnswer: string;
  isCompleted: boolean;
  feedback: string | undefined;
  isLoadingFeedback: boolean;
  onAnswerChange: (value: string) => void;
  onComplete: () => void;
  onPrevious: () => void;
  onNext: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
}

export const WorkoutExerciseContent = ({
  currentExercise,
  userAnswer,
  isCompleted,
  feedback,
  isLoadingFeedback,
  onAnswerChange,
  onComplete,
  onPrevious,
  onNext,
  canGoNext,
  canGoPrevious
}: WorkoutExerciseContentProps) => {
  return (
    <motion.div
      key={currentExercise.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="glass-card">
        <CardHeader>
          <div className="flex justify-between items-center mb-2">
            <CardTitle className="text-2xl font-lexend">{currentExercise.title}</CardTitle>
            <div className="flex items-center text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
              <Clock className="h-4 w-4 mr-1" />
              {currentExercise.timeEstimate}
            </div>
          </div>
          <CardDescription className="text-base">
            {currentExercise.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="exercise" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="exercise">Exercise</TabsTrigger>
              <TabsTrigger value="tips">Helpful Tips</TabsTrigger>
              <TabsTrigger value="feedback" disabled={!feedback}>
                AI Feedback
                {feedback && <Sparkles className="ml-1 h-3 w-3" />}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="exercise" className="space-y-4">
              <div className="min-h-[200px]">
                <Textarea
                  placeholder="Type your answer here..."
                  className="min-h-[200px] resize-none"
                  value={userAnswer || ''}
                  onChange={(e) => onAnswerChange(e.target.value)}
                />
              </div>
            </TabsContent>
            <TabsContent value="tips">
              <div className="p-4 bg-secondary/50 rounded-lg">
                <p className="text-muted-foreground">{currentExercise.tips}</p>
              </div>
            </TabsContent>
            <TabsContent value="feedback">
              {feedback ? (
                <div className="p-4 bg-primary/5 rounded-lg prose prose-sm max-w-none">
                  <div className="markdown-content">
                    <ReactMarkdown>
                      {feedback}
                    </ReactMarkdown>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-secondary/50 rounded-lg text-center">
                  <p className="text-muted-foreground">Complete the exercise to receive AI feedback</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
        <CardFooter className="flex justify-between border-t p-4">
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={onPrevious}
              disabled={!canGoPrevious}
            >
              Previous
            </Button>
            <Button 
              variant="outline" 
              onClick={onNext}
              disabled={!canGoNext}
            >
              Next
            </Button>
          </div>
          <Button 
            onClick={onComplete}
            disabled={isLoadingFeedback}
          >
            {isLoadingFeedback ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating Feedback
              </>
            ) : isCompleted ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Review Again
              </>
            ) : (
              <>
                Complete
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};
